import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import { Input, Row, Col, Card, CardBody, Button, Spinner } from "reactstrap"; // Added Spinner

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from "react-redux";
import Select from "@mui/material/Select";
import Multiselect from "multiselect-react-dropdown";
import { fetchParentCategories } from "../../../slices/thunks";

const defaultColor = "#000000"; // Default color for the color picker

const AddCategoryForm = ({ labels, formikProps, onTranslateClickHandler, translationLoading, lastTranslatedField, isAddOrEdit }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const isRTL = i18n.dir() === 'rtl'
    const today = new Date().toISOString().split("T")[0];
    const [showMultiselect, setShowMultiselect] = useState(true);
    
    // get parent categories 
  const parentCategories = useSelector((state) => state.Category.parentCategories);
  const documentsType = useSelector((state) => state.Category.documentsType);


  // Destructure from formikProps for easier access
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formikProps;
  
  useEffect(() => {
    dispatch( fetchParentCategories());
  }, []);

   
  useEffect(() => {
      setShowMultiselect(false); 

      const timer = setTimeout(() => {
          setShowMultiselect(true); 
      }, 0);

      return () => clearTimeout(timer); 

  }, [isAddOrEdit]); 

  const renderField = (item) => {
    const isDisabled = typeof item.disabled === 'function' ? item.disabled(isAddOrEdit) : !!item.disabled;
    const fieldValue = values[item.name] ?? "";

    switch (item.fieldName) {
      case "text-box":
        return (
            <Input
            type={"text"}
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={(e) => {
              const inputValue = e.target.value;
              
              // If it's RoleName field, allow only letters and spaces
              if (item.name === "RoleNameEn" || item.name === "RoleNameAr") {
                const cleanedValue = inputValue.replace(/[^A-Za-z\s]/g, '');
                setFieldValue(item.name, cleanedValue);
              } else {
                handleChange(e);
              }
            }}
            maxLength={20}
            minLength={2}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
            disabled={isDisabled}
            required={item.required}
          />
        );
    
      case "email-box":
        return (
          <Input
            type={"email"}
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
            required={item.required}
          />
        );

      case "number":
        return (
          <Input
            type="tel"
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={handleChange} 
            onBlur={handleBlur}
            className="form-control"
            // maxLength={maxLength}
            invalid={touched[item.name] && !!errors[item.name]}
            required={item.required}
          />
        );

      case 'number-box':
        return (
            <Input
                type="text"
                style={{ textAlign: isRTL ? 'right' : 'left' }}
                id={item.name}
                name={item.name}
                placeholder={t(item.placeholder || item.labelName)}
                value={fieldValue}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  let filteredValue = inputValue.replace(/[^0-9.]/g, '');

                  const parts = filteredValue.split('.');
                  if (parts.length > 2) {
                      filteredValue = parts[0] + '.' + parts.slice(1).join('');
                  }
                  setFieldValue(item.name, filteredValue);
                }}
                onBlur={handleBlur}
                className="form-control"
                invalid={touched[item.name] && !!errors[item.name]}
                disabled={isDisabled}
                required={item.required}
            />
        );


      case "text-box-translate":
        return (
          <div className="input-group"> 
            <Input
              type={"text"}
              id={item.name}
              name={item.name}
              placeholder={t(item.placeholder || item.labelName)}
              value={fieldValue}
              onChange={(e) => {
                const inputValue = e.target.value;
                
                // If it's RoleName field, allow only letters and spaces
                if (item.name === "RoleNameEn" || item.name === "RoleNameAr") {
                  const cleanedValue = inputValue.replace(/[^A-Za-z\s]/g, '');
                  setFieldValue(item.name, cleanedValue);
                } else {
                  handleChange(e);
                }
              }}            
              maxLength={20}
              minLength={2}
              onBlur={handleBlur}
              className="form-control"
              invalid={touched[item.name] && !!errors[item.name]}
              required={item.required}
            />
            {onTranslateClickHandler && item.translateTo && (
              <Button
                type="button"
                onClick={() => onTranslateClickHandler(item.translateTo, fieldValue, setFieldValue, item.name)} // Pass item.name as sourceFieldName
                disabled={!fieldValue || !fieldValue.trim() || translationLoading} // Disable if any translation is loading
              >
                {translationLoading && lastTranslatedField === item.name ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="ri-translate"></i>
                )}
              </Button>
            )}
          </div>
        );


      case "select-box-parent": {
          // Disable ParentCategory if CategoryType is 'Article' (2) or 'Book' (3)
          const isParentCategoryDisabled = isDisabled || values.CategoryType === '2' || values.CategoryType === '3';
          
          // Custom handler for Parent Category change
          const handleParentCategoryChange = (e) => {
            handleChange(e);

              if (isAddOrEdit === 'isAdd') {
                  const selectedParentId = e.target.value;

                  if (selectedParentId) {
                      const selectedCategory = parentCategories.find(cat => cat.ID == selectedParentId);
                      if (selectedCategory && Array.isArray(selectedCategory.DocumentType)) {
                          setFieldValue('DocumentType', selectedCategory.DocumentType);
                      } else {
                          setFieldValue('DocumentType', []);
                      }
                  } else {
                      // If "None" is selected, clear DocumentType and show all document types
                      setFieldValue('DocumentType', []);
                  }
              }
          };
          
          return (
              <Input
                  type="select"
                  id={item.name}
                  name={item.name}
                  value={fieldValue}
                  onChange={handleParentCategoryChange} // Use our custom handler here
                  onBlur={handleBlur}
                  className="form-control"
                  invalid={touched[item.name] && !!errors[item.name]}
                  disabled={isParentCategoryDisabled || isAddOrEdit === 'isEdit'}
                  required={item.required}
              >
                  <option value="">{t("None")}</option>
                  {parentCategories.map((option) => (
                  <option key={option.ID} value={option.ID}>
                      {isRTL ? t(option.NameAr) : option.NameEn}
                  </option>
                  ))}
              </Input>
          );
      }

      case "select-box":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
            disable={isAddOrEdit === 'edit'}
            required={item.required}
          >
            <option value="">{t("Select")} {t(item.labelName)}</option>
            {documentsType?.map((option) => (
              <option key={option.ID} value={option.ID}>
                {isRTL ? t(option.NameAr) : t(option.NameEn)}
              </option>
            ))}
          </Input>
        );
      
      case "multiselect-dropdown": {
            // Start with the full list of document types as the default.
            let documentTypeOptions = documentsType || [];
            
            const parentId = values.ParentCategory;
            
            // If a parent category IS selected, then filter the options.
            if (parentId) {
              
                const selectedCategory = parentCategories.find(cat => String(cat.ID) === String(parentId));
                
                // Check if the found parent has a specific list of document types.
                if (selectedCategory && Array.isArray(selectedCategory.DocumentType)) {
                    const allowedDocTypeIDs = selectedCategory.DocumentType;
                    documentTypeOptions = (documentsType || []).filter(doc => allowedDocTypeIDs.includes(doc.ID));
                } else {
                    // If the parent is selected but has no specified types, show no options.
                    documentTypeOptions = [];
                }
            }
            

            const selectedDocumentTypeValues = documentsType?.filter(doc =>
                Array.isArray(values[item.name]) && values[item.name].includes(doc.ID)
            ) || [];
            
            const isInvalid = touched[item.name] && !!errors[item.name];
            const multiselectCustomStyles = {
                multiselectContainer: { textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' },
                searchBox: {
                    paddingRight: isRTL ? '30px' : '',
                    textAlign: isRTL ? 'right' : 'left',
                    borderColor: isInvalid ? '#f46a6a' : undefined,
                },
                chips: { direction: isRTL ? 'rtl' : 'ltr', justifyContent: isRTL ? 'flex-end' : 'flex-start' },
                optionContainer: { textAlign: isRTL ? 'right' : 'left' },
            };
            
            const isMultiselectDisabled = isAddOrEdit === 'isEdit' || (isAddOrEdit === 'isAdd' && !!values.ParentCategory);
          
            return (
                <>
                    {showMultiselect &&
                      <div dir={isRTL ? 'rtl' : 'ltr'}>
                          <Multiselect
                              key={`${isAddOrEdit}-${values.ParentCategory || 'no-parent'}`}
                              options={documentTypeOptions}
                              selectedValues={selectedDocumentTypeValues}
                              onSelect={(selectedList) => {
                                  const newValues = selectedList.map(option => option.ID);
                                  setFieldValue(item.name, newValues, true);
                              }}
                              onRemove={(selectedList) => {
                                  const newValues = selectedList.map(option => option.ID);
                                  setFieldValue(item.name, newValues, true);
                              }}
                              displayValue={isRTL ? 'NameAr' : 'NameEn'}
                              placeholder={t(item.placeholder || "SelectDocumentTypes")}
                              disable={isMultiselectDisabled}
                              emptyRecordMsg={t("NoOptions", { defaultValue: "No options available" })}
                              style={multiselectCustomStyles}
                          />
                      </div>
                    }
                    {/* Hidden input for validation */}
                    <input
                        type="text" tabIndex={-1} autoComplete="off"
                        value={(values[item.name] || []).length > 0 ? 'selected' : ''}
                        onChange={() => {}} required={item.required} disabled={isMultiselectDisabled}
                        style={{ opacity: 0, height: 0, width: 0, position: 'absolute', pointerEvents: 'none' }}
                        onFocus={() => setFieldTouched(item.name, true)}
                    />
                </>
            );
      }

      case "select-box-parent": {
          const isParentCategoryDisabled = isDisabled || values.CategoryType === '2' || values.CategoryType === '3';
          
          const handleParentCategoryChange = (e) => {
              // First, update the formik state for the parent category itself
              handleChange(e);

              // This logic should only apply when adding a new category
              if (isAddOrEdit === 'isAdd') {
                  const selectedParentId = e.target.value;
                  
                  // If a parent is selected, find its document types and set the multiselect value
                  if (selectedParentId) {
                      const selectedCategory = parentCategories.find(cat => String(cat.ID) === String(selectedParentId));
                      if (selectedCategory && Array.isArray(selectedCategory.DocumentType)) {
                          setFieldValue('DocumentType', selectedCategory.DocumentType);
                      } else {
                          // If parent has no types, clear the selection
                          setFieldValue('DocumentType', []);
                      }
                  } else {
                      // If "None" is selected, clear the DocumentType selection
                      setFieldValue('DocumentType', []);
                  }
              }
          };
          
          return (
              <Input
                  type="select"
                  id={item.name}
                  name={item.name}
                  value={fieldValue}
                  onChange={handleParentCategoryChange}
                  onBlur={handleBlur}
                  className="form-control"
                  invalid={touched[item.name] && !!errors[item.name]}
                  disabled={isParentCategoryDisabled || isAddOrEdit === 'isEdit'}
                  required={item.required}
              >
                  <option value="">{t("None")}</option>
                  {(parentCategories || []).map((option) => (
                    <option key={option.ID} value={option.ID}>
                        {isRTL ? t(option.NameAr) : option.NameEn}
                    </option>
                  ))}
              </Input>
          );
      }

      case "color-picker": // Added case for color picker
          return (
            <Input
              type="color"
              id={item.name}
              name={item.name}
              placeholder={t(item.placeholder || item.labelName)}
              value={fieldValue || defaultColor} // Use defaultColor if value is empty
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-control form-control-color" // Use form-control-color class for styling
              invalid={touched[item.name] && !!errors[item.name]}
              required={item.required}
            />
          );
        default:
          return null;
    }
  };

  return (
    <Row>
      {labels.map((item) => {
        
        return (
          <Col sm={6} key={item.name}>
            <div className="mb-3">
              {item.labelName !== "Mobile" && (
                <label htmlFor={item.name} className="col-form-label">
                  {t(item.labelName)}
                  {item.required && <span className="text-danger"> *</span>}
                </label>
              )} 
              {/* Render the field */}
              {renderField(item)}
              {/* Display error for the main field */}
              {item.labelName !== "Mobile" && touched[item.name] && errors[item.name] && (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(errors[item.name])}</div>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
}

export default AddCategoryForm